import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'

// ---- Wire types derived from the contract (single source of truth) --------------------

export type CpgDocumentDetail = Awaited<ReturnType<OrpcClient['getCpgDocument']>>['data']
export type CpgAssignment = CpgDocumentDetail['assignments'][number]
export type CpgUnitView = CpgDocumentDetail['units'][number]
export type CpgDocumentStatus = CpgDocumentDetail['status']
export type CpgDocumentListItem = Awaited<
  ReturnType<OrpcClient['listCpgDocuments']>
>['data'][number]
export type CpgFamilyGroup = Awaited<ReturnType<OrpcClient['listCpgFamilies']>>['data'][number]
export type CpgFamily = CpgFamilyGroup['families'][number]
export type CpgSourceSummary = Awaited<ReturnType<OrpcClient['listCpgSources']>>['data'][number]
export type CpgXwalkRuleRow = Awaited<ReturnType<OrpcClient['listCpgXwalkRules']>>['data'][number]
export type CpgMeasure = Awaited<ReturnType<OrpcClient['listCpgMeasures']>>['data'][number]
export type CpgAuditRow = Awaited<ReturnType<OrpcClient['listCpgAudit']>>['data'][number]
export type CpgProposalCluster = Awaited<
  ReturnType<OrpcClient['listCpgProposalClusters']>
>['data'][number]
export type CpgMeasureProposal = Awaited<ReturnType<OrpcClient['listCpgProposals']>>['data'][number]

// ---- Display vocab ---------------------------------------------------------------------

/** Mantine Badge colors per document status (lifecycle order). */
export const DOCUMENT_STATUS_COLORS: Record<CpgDocumentStatus, string> = {
  pending_classification: 'gray',
  needs_registration: 'blue',
  active: 'green',
  superseded: 'yellow',
  withdrawn: 'red',
}

/** Mantine Badge colors per measure status (docs/GLOSSARY.md "Measure"). */
export const MEASURE_STATUS_COLORS: Record<CpgMeasure['status'], string> = {
  active: 'green',
  retired: 'gray',
}

/** Mantine Badge colors per proposal cluster status (docs/GLOSSARY.md "Proposal cluster"). */
export const CLUSTER_STATUS_COLORS: Record<CpgProposalCluster['status'], string> = {
  recommended: 'green',
  'below-threshold': 'gray',
  accepted: 'blue',
  dismissed: 'red',
}

export const MEASURE_KIND_COLORS: Record<CpgMeasure['kind'], string> = {
  risk: 'orange',
  gap: 'teal',
}

export const UNIT_KIND_COLORS: Record<CpgUnitView['kind'], string> = {
  recommendation: 'blue',
  clinical: 'teal',
  rationale: 'grape',
  discussion: 'gray',
}

/** Normalized recommendation strength (grade letter definitions vary by source). */
export const UNIT_GRADE_COLORS: Record<NonNullable<CpgUnitView['grade']>, string> = {
  strong: 'indigo',
  conditional: 'yellow',
  expert: 'gray',
}

const BMI_LABELS: Record<string, string> = {
  UW: 'underweight',
  N: 'normal',
  O: 'overweight',
  OB: 'obese',
}

/**
 * The unit's population requirements as a compact line: age window, sex, and risk flags
 * (vocabulary in middleware's risk-flags.ts). Empty string = the unit doesn't condition
 * on population at all.
 */
export function unitPopulationLabel(unit: CpgUnitView): string {
  const parts: string[] = []
  if (unit.ageMin !== null && unit.ageMax !== null) parts.push(`ages ${unit.ageMin}–${unit.ageMax}`)
  else if (unit.ageMin !== null) parts.push(`ages ${unit.ageMin}+`)
  else if (unit.ageMax !== null) parts.push(`up to age ${unit.ageMax}`)
  if (unit.sex) parts.push(unit.sex)
  const flags = unit.riskFlags ?? {}
  if (flags.tobacco) parts.push('tobacco user')
  if (flags.pregnant) parts.push('pregnant')
  if (flags.sexuallyActive) parts.push('sexually active')
  if (typeof flags.bmi === 'string') parts.push(`BMI ${BMI_LABELS[flags.bmi] ?? flags.bmi}`)
  return parts.join(' · ')
}

/** 'Practitioner/abc-123' → 'abc-123' (references are Medplum profile refs). */
export function actorLabel(reference: string | null): string {
  if (!reference) return '—'
  return reference.split('/').pop() ?? reference
}

// ---- Queries ---------------------------------------------------------------------------

export function useCpgDocument(id: number | null) {
  const orpc = useOrpc()
  return useQuery(
    orpc.getCpgDocument.queryOptions({
      input: { id: id ?? 0 },
      enabled: id !== null,
    }),
  )
}

export function useCpgFamilies() {
  const orpc = useOrpc()
  return useQuery(orpc.listCpgFamilies.queryOptions({ input: {}, staleTime: 30_000 }))
}

export function useCpgMeasures() {
  const orpc = useOrpc()
  return useQuery(orpc.listCpgMeasures.queryOptions({ input: {}, staleTime: 30_000 }))
}

export function useCpgProposalClusters(state: 'open' | 'accepted' | 'dismissed') {
  const orpc = useOrpc()
  return useQuery(orpc.listCpgProposalClusters.queryOptions({ input: { state } }))
}

/** Member proposals per page — the list endpoint's maximum. */
export const CLUSTER_PROPOSALS_PAGE = 100

/** One page of a cluster's member proposals; `page.more` says whether another follows. */
export function useCpgClusterProposals(clusterId: number, offset: number) {
  const orpc = useOrpc()
  return useQuery(
    orpc.listCpgProposals.queryOptions({
      input: {
        page: { offset, count: CLUSTER_PROPOSALS_PAGE },
        filters: [{ field: 'clusterId', operator: 'eq', operand: String(clusterId) }],
      },
    }),
  )
}

export function useCpgSources() {
  const orpc = useOrpc()
  return useQuery(orpc.listCpgSources.queryOptions({ input: {}, refetchInterval: 60_000 }))
}
